import React, { useEffect, useMemo, useState } from "react"
import { Button, DatePicker, Drawer, Radio, Spin } from "antd"
import { useQuery } from "@tanstack/react-query"
import { useHistory } from "react-router-dom"
import moment from "moment"
import useBreakpoint from "antd/lib/grid/hooks/useBreakpoint"
import i18n from "../../services/i18n"
import { request } from "../../utils/config"
import getDateInterval from "../../utils/getDateInterval"
import hasPermission from "../../utils/hasPermission"
import { expenseTypeLabel } from "../../utils/expenseTypeLabel"
import "./moneyTodayDrawer.less"

// tog'a #4127 (2026-10-10, «+++ tavsiyang bajar» #4132): «Деньги сегодня» — ichiga kirganda aynan qaysi rasxod yoki doxoddan kelgani
// (tur → aniq yozuvlar) va har tur / yozuvdan TO'G'RI sahifaga: xarajat → «Расходы» (shu kun + shu tur), kassa → «Деньги» (shu kun + shu maqsad).
// Raqamlar kartadagi bilan AYNAN bir xil: karta xulosasi (Wallet/Summary) + Wallet/Rows `asSummary=true` — inkassatsiya, kurs farqi,
// rad etilgan to'lov ikkalasida ham yo'q (server: WalletRowsTests). Sahifa huquqi bo'lmasa havola chiqmaydi (yangi yo'l ochilmaydi).
// tog'a #4366 (2026-10-14): «doxod va rasxodni alohida qilib ber, tartib bilan chiqsin» — ikki alohida varaq «⬇ Доход» / «⬆ Расход»
// (karta qatori yoki tur bosilsa — o'sha varaq), ichida «По группам» (eng kattasidan) yoki «По времени» (eng yangisidan, bitta lenta).
// Ranglar #4363: rasxod — qizil, prixod — ko'k.
// tog'a #4386 (2026-10-14): «shuni ichiga vaqt interval qo'yilsin» — oynada davr tanlovi (sukut — bugun; «Вчера / Неделя / Месяц» tez tugmalari).
// Bugundan boshqa davrda xulosa (Wallet/Summary) shu oynaning o'zi so'raydi — karta raqamlari bilan bir xil ta'rif. #4385: «Доход» → «Приход».

const usd = (v: any) => "$" + (Number(v) || 0).toLocaleString("ru-RU", { maximumFractionDigits: 2 })
const som = (v: any) => Math.round(Number(v) || 0).toLocaleString("ru-RU") + " " + i18n.t("sumShort", "сум")
const vaqt = (v: any, kunlik = true) => (v ? moment(v).format(kunlik ? "HH:mm" : "DD.MM HH:mm") : "")
const yangiBirinchi = (a: any, b: any) => String(b.date || "").localeCompare(String(a.date || ""))
const currentUser = (): any => { try { return JSON.parse(localStorage.getItem("user") || "null") } catch { return null } }

/** Karta va oyna uchun umumiy: xarajat turi guruhi kaliti (server `key`; eski javobda — `code`). */
export const turKaliti = (g: any) => String(g?.key ?? g?.code ?? "other")
/** To'lov maqsadi guruhi kaliti (maqsadsiz = -1, server filtri bilan bir xil). */
export const maqsadKaliti = (id: any) => (id == null ? -1 : Number(id))

type Props = { open: boolean; focus?: string; summary: any; day: string; onClose: () => void }

export default function MoneyTodayDrawer({ open, focus, summary, day, onClose }: Props) {
  // #4386: davr — sukut bo'yicha kartadagi kun; oyna har ochilganda shu kunga qaytadi
  const [oraliq, setOraliq] = useState<[string, string]>([day, day])
  useEffect(() => { if (open) setOraliq([day, day]) }, [open, day])
  const [b, e] = oraliq
  const bugunmi = b === day && e === day
  const birKun = b === e
  const sumQ = useQuery(["MT_SUMMARY", b, e], async () => (await request({ url: "Wallet/Summary", params: { begin: b, end: e } })).data, { enabled: open && !bugunmi, keepPreviousData: true })
  const history = useHistory()
  const bp = useBreakpoint()
  const tor = bp.md === false
  const user = currentUser()
  const xarajatOk = hasPermission(user, "/expenses")
  const pulOk = hasPermission(user, "/moneys")

  const qatorlar = (direction: string) => async () =>
    (await request({ url: "Wallet/Rows", params: { begin: b, end: e, direction, asSummary: true, take: 1000 } })).data?.items || []
  const ex = useQuery(["MT_ROWS", "expense", b, e], qatorlar("expense"), { enabled: open, staleTime: 10000 })
  const out = useQuery(["MT_ROWS", "out", b, e], qatorlar("out"), { enabled: open, staleTime: 10000 })
  const inn = useQuery(["MT_ROWS", "in", b, e], qatorlar("in"), { enabled: open, staleTime: 10000 })
  const yuklanmoqda = ex.isLoading || out.isLoading || inn.isLoading || (!bugunmi && sumQ.isLoading)
  const kirimFokus = (f?: string) => f === "in" || String(f || "").startsWith("i-")
  const [varaq, setVaraq] = useState<"in" | "out">(kirimFokus(focus) || !focus ? "in" : "out")
  const [tartib, setTartib] = useState<"grp" | "time">("grp")
  useEffect(() => { if (open) setVaraq(kirimFokus(focus) || !focus ? "in" : "out") }, [open, focus])

  // «Деньги» sahifasi sana filtrini begin/end (ofsetli ISO) dan o'qiydi — LiveCounters dagi bugunPul bilan bir xil
  const pulHavola = (purposeId?: number, counterpartyId?: number) => {
    const { begin, end } = getDateInterval(moment(b, "YYYY-MM-DD"), moment(e, "YYYY-MM-DD"))
    const p: Record<string, string> = { begin, end }
    if (purposeId != null && purposeId > 0) p.purposePaymentId = String(purposeId)
    if (counterpartyId != null && counterpartyId > 0) p.counterpartyId = String(counterpartyId)
    return "/moneys?" + new URLSearchParams(p).toString()
  }
  // «Расходы» sahifasi (#4127): begin/end (YYYY-MM-DD), typeCode, document
  const xarajatHavola = (typeCode?: string, document?: string) => {
    const p: Record<string, string> = { begin: b, end: e }
    if (typeCode) p.typeCode = typeCode
    if (document && !document.includes(",")) p.document = document   // bir nechta konteynerga bo'lingan to'lov — hujjatsiz (tur bo'yicha)
    return "/expenses?" + new URLSearchParams(p).toString()
  }
  const ot = (url: string) => { onClose(); history.push(url) }

  const S = (bugunmi ? summary : sumQ.data) || {}
  const xarajatGuruh = useMemo(() => (S.expenseByType || []).map((g: any) => ({
    kalit: turKaliti(g), nom: expenseTypeLabel(g.code, g.code), amount: Number(g.amount) || 0,
    items: (ex.data || []).filter((r: any) => String(r.typeKey ?? r.type ?? "other") === turKaliti(g) || (r.typeKey == null && r.type === g.code)).sort(yangiBirinchi),
  })).sort((a: any, b: any) => b.amount - a.amount), [S.expenseByType, ex.data])
  const maqsadGuruh = (list: any[], rows: any[]) => (list || []).map((g: any) => ({
    kalit: maqsadKaliti(g.purposeId), nom: g.purposeId == null ? i18n.t("mtNoPurpose", "Без назначения") : g.name, amount: Number(g.amount) || 0,
    items: (rows || []).filter((r: any) => (r.purposeId !== undefined ? maqsadKaliti(r.purposeId) === maqsadKaliti(g.purposeId) : (r.purpose || "—") === g.name)).sort(yangiBirinchi),
  })).sort((a: any, b: any) => b.amount - a.amount)
  const chiqimGuruh = useMemo(() => maqsadGuruh(S.outcomeByPurpose, out.data), [S.outcomeByPurpose, out.data])   // eslint-disable-line react-hooks/exhaustive-deps
  const kirimGuruh = useMemo(() => maqsadGuruh(S.incomeByPurpose, inn.data), [S.incomeByPurpose, inn.data])   // eslint-disable-line react-hooks/exhaustive-deps

  // Kartadagi tur yoki qator bosilgan bo'lsa — oyna o'sha joyga suriladi
  useEffect(() => {
    if (!open || !focus || yuklanmoqda) return
    const t = setTimeout(() => { try { document.getElementById("mt-" + focus)?.scrollIntoView({ block: "start", behavior: "smooth" }) } catch {} }, 150)
    return () => clearTimeout(t)
  }, [open, focus, yuklanmoqda, varaq])

  const xarajatUSD = Number(S.outcomeExpense || 0)
  const chiqimJami = Number(S.outcomeUSD || 0) + xarajatUSD
  const chiqimRow = (r: any, yo: "in" | "out") => {
    const ichi = (
      <>
        <span className="mt-time">{vaqt(r.date, birKun)}</span>
        <span className="mt-main">
          {r.document ? <b>{r.document}</b> : null}
          {r.counterparty ? <span> · {r.counterparty}</span> : null}
          {r.paymentType ? <span className="mt-muted"> · {r.paymentType}</span> : null}
          {r.pending ? <span className="mt-muted"> · {i18n.t("mtPending", "ждёт подтверждения")}</span> : null}
        </span>
        <span className={yo === "in" ? "mt-sum mt-in" : "mt-sum mt-out"}>
          {usd(r.amountUSD)}{r.currency === "UZS" && Number(r.paidUZS) > 0 ? <span className="mt-muted"> · {som(r.paidUZS)}</span> : null}
        </span>
      </>
    )
    return pulOk
      ? <button type="button" key={"m" + r.id} className="mt-item mt-link" onClick={() => ot(pulHavola(r.purposeId, r.counterpartyId))} data-testid="mt-money-item">{ichi}</button>
      : <div key={"m" + r.id} className="mt-item">{ichi}</div>
  }
  const xarajatRow = (r: any, kalit: string) => {
    const ichi = (
      <>
        <span className="mt-time">{vaqt(r.date, birKun)}</span>
        <span className="mt-main">{r.document ? <b>{r.document}</b> : null}{r.description ? <span className="mt-muted"> · «{r.description}»</span> : null}</span>
        <span className="mt-sum mt-out">{usd(r.amountUSD)}{Number(r.amountUZS) > 0 ? <span className="mt-muted"> · {som(r.amountUZS)}</span> : null}</span>
      </>
    )
    return xarajatOk
      ? <button type="button" key={"x" + r.id} className="mt-item mt-link" onClick={() => ot(xarajatHavola(kalit, r.document))} data-testid="mt-expense-item">{ichi}</button>
      : <div key={"x" + r.id} className="mt-item">{ichi}</div>
  }
  const lenta = (yo: "in" | "out") => {
    const q = yo === "in"
      ? (inn.data || []).map((r: any) => ({ r, tur: "m" }))
      : [...(ex.data || []).map((r: any) => ({ r, tur: "x" })), ...(out.data || []).map((r: any) => ({ r, tur: "m" }))]
    q.sort((a: any, b: any) => yangiBirinchi(a.r, b.r))
    return q.length
      ? <div className="mt-grp" data-testid="mt-timeline">{q.map((x: any) => (x.tur === "x" ? xarajatRow(x.r, String(x.r.typeKey ?? x.r.type ?? "other")) : chiqimRow(x.r, yo)))}</div>
      : <div className="mt-empty">{yo === "in" ? i18n.t("mtNoIncome", "Сегодня поступлений не было") : i18n.t("mtNoExpenses", "Сегодня расходов не было")}</div>
  }
  const guruhBlok = (g: any, id: string, rang: "in" | "out", havola: string | null, havolaNom: string, row: (r: any) => React.ReactNode) => (
    <div className="mt-grp" id={"mt-" + id} key={id} data-testid="mt-group">
      <div className="mt-gh">
        <span className="mt-gname">{g.nom}</span>
        <span className="mt-gright">
          <b className={rang === "in" ? "mt-in" : "mt-out"}>{usd(g.amount)}</b>
          {havola ? <button type="button" className="mt-open" onClick={() => ot(havola)} data-testid="mt-open-link">{havolaNom} ›</button> : null}
        </span>
      </div>
      {g.items.length ? g.items.map(row) : <div className="mt-item mt-muted">{yuklanmoqda ? "…" : i18n.t("mtNoRows", "Записи не найдены")}</div>}
    </div>
  )

  return (
    <Drawer
      visible={open}
      onClose={onClose}
      placement="right"
      width={tor ? "100%" : 720}
      destroyOnClose
      className="money-today-drawer"
      title={`${bugunmi ? i18n.t("liveMoneyToday", "Деньги сегодня") : i18n.t("moneys", "Деньги")} — ${birKun ? moment(b, "YYYY-MM-DD").format("DD.MM.YYYY") : moment(b, "YYYY-MM-DD").format("DD.MM") + " – " + moment(e, "YYYY-MM-DD").format("DD.MM.YYYY")}${S.scope === "mine" ? " · " + i18n.t("myWallet", "Мой кошелёк") : ""}`}
      footer={
        <div className="mt-foot">
          {xarajatOk && <Button type="primary" onClick={() => ot(xarajatHavola())} data-testid="mt-open-expenses">{i18n.t(bugunmi ? "mtOpenToday" : "mtOpenPeriod", bugunmi ? "Открыть «{{s}}» за сегодня" : "Открыть «{{s}}» за период", { s: i18n.t("expenses", "Расходы") })}</Button>}
          {pulOk && <Button onClick={() => ot(pulHavola())} data-testid="mt-open-moneys">{i18n.t(bugunmi ? "mtOpenToday" : "mtOpenPeriod", bugunmi ? "Открыть «{{s}}» за сегодня" : "Открыть «{{s}}» за период", { s: i18n.t("moneys", "Деньги") })}</Button>}
        </div>
      }
    >
      <div data-testid="money-today-drawer">
        <div className="mt-tot">
          <div className="mt-pill"><div className="mt-k">{i18n.t("income", "Приход")}</div><div className="mt-v mt-in">{usd(S.incomeUSD)}{Number(S.incomeUZS) > 0 ? <small> · {som(S.incomeUZS)}</small> : null}</div></div>
          <div className="mt-pill"><div className="mt-k">{i18n.t("expense", "Расход")}</div><div className="mt-v mt-out" data-testid="mt-total-out">{usd(chiqimJami)}{Number(S.outcomeUZS) > 0 ? <small> · {som(S.outcomeUZS)}</small> : null}</div></div>
          <div className="mt-pill"><div className="mt-k">{i18n.t("mtTotal", "Итого")}</div><div className={"mt-v " + (Number(S.income || 0) - Number(S.outcome || 0) >= 0 ? "mt-in" : "mt-out")}>{(Number(S.income || 0) - Number(S.outcome || 0) < 0 ? "−" : "") + usd(Math.abs(Number(S.income || 0) - Number(S.outcome || 0)))}</div></div>
        </div>
        <div className="mt-bar">
          <Radio.Group value={varaq} onChange={(e) => setVaraq(e.target.value)} buttonStyle="solid" className="mt-tabs" data-testid="mt-tabs">
            <Radio.Button value="in" className="mt-tab-in" data-testid="mt-tab-in">⬇ {i18n.t("income", "Приход")} <b>{usd(S.income)}</b></Radio.Button>
            <Radio.Button value="out" className="mt-tab-out" data-testid="mt-tab-out">⬆ {i18n.t("expense", "Расход")} <b>{usd(S.outcome)}</b></Radio.Button>
          </Radio.Group>
          <Radio.Group value={tartib} onChange={(e) => setTartib(e.target.value)} size="small" data-testid="mt-order">
            <Radio.Button value="grp">{i18n.t("mtByGroup", "По группам")}</Radio.Button>
            <Radio.Button value="time">{i18n.t("mtByTime", "По времени")}</Radio.Button>
          </Radio.Group>
          {/* #4386: davr tanlovi */}
          <DatePicker.RangePicker size="small" allowClear={false} format="DD.MM.YYYY" data-testid="mt-range" className="mt-range"
            value={[moment(b, "YYYY-MM-DD"), moment(e, "YYYY-MM-DD")] as any}
            disabledDate={(d: any) => d && d.isAfter(moment(), "day")}
            ranges={{
              [i18n.t("today", "Сегодня")]: [moment(), moment()],
              [i18n.t("yesterday", "Вчера")]: [moment().subtract(1, "day"), moment().subtract(1, "day")],
              [i18n.t("week", "Неделя")]: [moment().startOf("isoWeek"), moment()],
              [i18n.t("month", "Месяц")]: [moment().startOf("month"), moment()],
            } as any}
            onChange={(v: any) => { if (v && v[0] && v[1]) setOraliq([v[0].format("YYYY-MM-DD"), v[1].format("YYYY-MM-DD")]) }} />
        </div>
        <Spin spinning={yuklanmoqda}>
          {varaq === "in" ? (
          <section className="mt-sec" id="mt-in">
            <h3><span>{i18n.t("mtIncomeByPurpose", "Доход — по назначению")} <span className="mt-muted">({i18n.t("mtSection", "раздел «{{s}}»", { s: i18n.t("moneys", "Деньги") })})</span></span><b className="mt-in">{usd(S.income)}</b></h3>
            {tartib === "time" ? lenta("in") : kirimGuruh.length
              ? kirimGuruh.map((g: any) => guruhBlok(g, "i-" + g.kalit, "in", pulOk ? pulHavola(g.kalit) : null, i18n.t("mtOpenIn", "Открыть в «{{s}}»", { s: i18n.t("moneys", "Деньги") }), (r) => chiqimRow(r, "in")))
              : <div className="mt-empty">{i18n.t("mtNoIncome", "Сегодня поступлений не было")}</div>}
          </section>
          ) : tartib === "time" ? (
          <section className="mt-sec" id="mt-out">
            <h3><span>{i18n.t("expense", "Расход")} <span className="mt-muted">({i18n.t("mtByTime", "По времени")})</span></span><b className="mt-out">{usd(S.outcome)}</b></h3>
            {lenta("out")}
          </section>
          ) : (<>
          {/* tog'a #4371: kassadan bankka inkassatsiya — rasxod emas, lekin kassadan chiqqani ko'rinsin */}
          {Number(S.transfers?.outUSD) > 0 ? <div className="mt-empty" data-testid="mt-incasso">🏦 {i18n.t("walletIncassoLine", "Инкассация в банк (не расход)")}: <b>{usd(S.transfers.outUSD)}</b></div> : null}
          <section className="mt-sec" id="mt-out">
            <h3><span>{i18n.t("mtExpensesByType", "Расходы — по типам")} <span className="mt-muted">({i18n.t("mtSection", "раздел «{{s}}»", { s: i18n.t("expenses", "Расходы") })})</span></span><b className="mt-out">{usd(xarajatUSD)}</b></h3>
            {xarajatGuruh.length
              ? xarajatGuruh.map((g: any) => guruhBlok(g, "x-" + g.kalit, "out", xarajatOk ? xarajatHavola(g.kalit) : null, i18n.t("mtOpenIn", "Открыть в «{{s}}»", { s: i18n.t("expenses", "Расходы") }), (r) => xarajatRow(r, g.kalit)))
              : <div className="mt-empty">{i18n.t("mtNoExpenses", "Сегодня расходов не было")}</div>}
          </section>
          <section className="mt-sec">
            <h3><span>{i18n.t("mtCashOut", "Выплаты из кассы — по назначению")} <span className="mt-muted">({i18n.t("mtSection", "раздел «{{s}}»", { s: i18n.t("moneys", "Деньги") })})</span></span><b className="mt-out">{usd(S.outcomeCash)}</b></h3>
            {chiqimGuruh.length
              ? chiqimGuruh.map((g: any) => guruhBlok(g, "o-" + g.kalit, "out", pulOk ? pulHavola(g.kalit) : null, i18n.t("mtOpenIn", "Открыть в «{{s}}»", { s: i18n.t("moneys", "Деньги") }), (r) => chiqimRow(r, "out")))
              : <div className="mt-empty">{i18n.t("mtNoCashOut", "Сегодня выплат из кассы не было")}</div>}
          </section>
          </>)}
        </Spin>
      </div>
    </Drawer>
  )
}
